import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';

const ListaAcordos = () => {
    const navigate = useNavigate();
    const [acordos, setAcordos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Busca a lista de acordos gerados pela empresa logada
        api.get('/assinante/acordos')
            .then(res => {
                setAcordos(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Erro ao buscar acordos:", err);
                setLoading(false);
            });
    }, []);

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', { 
            style: 'currency', 
            currency: 'BRL' 
        }).format(valor || 0);
    };

    const formatarData = (dataIso) => {
        if (!dataIso) return '--/--/----';
        const data = new Date(dataIso);
        return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute:'2-digit' });
    };

    const copiarLink = (acordoId) => {
        const urlPublica = `${window.location.origin}/acordo/${acordoId}`;
        navigator.clipboard.writeText(urlPublica);
        alert("Link copiado! Podes enviar novamente pelo WhatsApp.");
    };

    // Função para renderizar o "badge" de status com as cores certas
    const renderStatus = (status) => {
        let corBg = 'rgba(255, 255, 255, 0.1)';
        let corTexto = '#94a3b8';
        let texto = status;

        if (status === 'AGUARDANDO_PAGAMENTO' || status === 'PROPOSTA_ENVIADA') {
            corBg = 'rgba(245, 158, 11, 0.1)';
            corTexto = '#f59e0b'; // Âmbar
            texto = 'Aguardando Pagamento';
        } else if (status === 'PAGO') {
            corBg = 'rgba(16, 185, 129, 0.1)';
            corTexto = '#10b981'; // Verde
            texto = 'Pago / Liquidado';
        } else if (status === 'CANCELADO' || status === 'QUEBRADO') {
            corBg = 'rgba(239, 68, 68, 0.1)';
            corTexto = '#ef4444'; // Vermelho
            texto = 'Acordo Quebrado';
        }

        return (
            <span style={{ 
                background: corBg, color: corTexto, 
                padding: '5px 10px', borderRadius: '20px', 
                fontSize: '0.75rem', fontWeight: 'bold' 
            }}>
                {texto}
            </span>
        );
    };

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                
                <div style={styles.header}>
                    <button onClick={() => navigate('/dashboard')} style={styles.btnVoltar}>
                        ← Voltar ao Dashboard
                    </button>
                    <h1 style={styles.title}>Meus Acordos 🤝</h1>
                    <p style={styles.subtitle}>Acompanhe as negociações e links de pagamento gerados.</p>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>A carregar acordos...</div>
                ) : acordos.length === 0 ? (
                    <div style={styles.emptyState}>
                        <span style={{ fontSize: '3rem', display: 'block', marginBottom: '15px' }}>📭</span>
                        <h3>Nenhum acordo gerado</h3>
                        <p>Ainda não geraste nenhuma proposta de negociação para os teus devedores.</p>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {acordos.map(acordo => (
                            <div key={acordo.id} style={styles.card}>
                                
                                <div style={styles.cardHeader}>
                                    <div>
                                        <h3 style={styles.devedorNome}>{acordo.devedorNome || 'Devedor não informado'}</h3>
                                        <p style={styles.credorNome}>{acordo.credorNome || 'Credor'}</p>
                                    </div>
                                    {renderStatus(acordo.statusAcordo)}
                                </div>

                                <div style={styles.cardBody}>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Valor Acordado:</span>
                                        <strong style={styles.infoValueHighlight}>{formatarMoeda(acordo.valorAcordado)}</strong>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Parcelas:</span>
                                        <strong style={styles.infoValue}>{acordo.parcelas === 1 ? 'À vista (PIX)' : `${acordo.parcelas}x`}</strong>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span style={styles.infoLabel}>Gerado em:</span>
                                        <strong style={styles.infoValue}>{formatarData(acordo.dataCriacao)}</strong>
                                    </div>
                                    
                                    {/* Mostrar evidências jurídicas caso já tenha sido assinado */}
                                    {acordo.aceitoEm && (
                                        <div style={styles.assinaturaBox}>
                                            <p style={{ margin: '0 0 5px 0', fontSize: '0.8rem', color: '#10b981', fontWeight: 'bold' }}>✓ Assinado Digitalmente</p>
                                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>IP: {acordo.ipAssinatura}</p>
                                        </div>
                                    )}
                                </div>

                                <div style={styles.cardFooter}>
                                        <button onClick={() => copiarLink(acordo.tokenAcesso)} style={styles.btnSecondary}>
                                            🔗 Copiar Link
                                        </button>
                                        <button 
                                            onClick={() => window.open(`https://wa.me/?text=Olá! Segue o link atualizado do nosso acordo para pagamento: ${window.location.origin}/acordo/${acordo.tokenAcesso}`, '_blank')}
                                            style={styles.btnPrimary}
                                        >
                                            WhatsApp
                                        </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: { minHeight: '100vh', background: '#020617', padding: '20px', fontFamily: 'sans-serif' },
    content: { maxWidth: '1200px', margin: '0 auto' },
    
    header: { marginBottom: '30px' },
    btnVoltar: { background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer', fontSize: '0.95rem', marginBottom: '15px', padding: 0 },
    title: { color: 'white', fontSize: '2rem', margin: '0 0 5px 0' },
    subtitle: { color: '#94a3b8', margin: 0 },

    emptyState: { textAlign: 'center', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '12px', padding: '50px 20px', color: '#94a3b8' },

    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' },
    card: { background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', display: 'flex', flexDirection: 'column' },
    
    cardHeader: { padding: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    devedorNome: { color: 'white', margin: '0 0 5px 0', fontSize: '1.1rem' },
    credorNome: { color: '#64748b', margin: 0, fontSize: '0.85rem' },

    cardBody: { padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' },
    infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    infoLabel: { color: '#94a3b8', fontSize: '0.9rem' },
    infoValue: { color: 'white', fontSize: '0.9rem' },
    infoValueHighlight: { color: '#10b981', fontSize: '1.1rem' },

    assinaturaBox: { background: 'rgba(16, 185, 129, 0.05)', borderLeft: '3px solid #10b981', padding: '10px', marginTop: '10px', borderRadius: '0 4px 4px 0' },

    cardFooter: { padding: '15px 20px', background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '0 0 12px 12px', display: 'flex', gap: '10px' },
    
    btnSecondary: { flex: 1, background: 'rgba(255, 255, 255, 0.05)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.1)', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', transition: 'background 0.2s' },
    btnPrimary: { flex: 1, background: '#10b981', color: '#022c22', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', transition: 'opacity 0.2s' },
};

export default ListaAcordos;